import { Stack, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { IssueRelation, Quantum } from "../../shared/CaseModel";
import { percentage } from "../../shared/percentage";
import { NO_VALUE, TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { ClaimTableHead } from "./ClaimTableHead";
import { IssueGroupHeaderRow } from "./IssueGroupHeaderRow";
import {
  Defendant,
  Issue,
  IssueGroup,
  RowChances,
  getXorWinChanceLimits,
  isGroup,
} from "./IssueModel";
import { IssueRelationSelector } from "./IssueRelationSelector";
import { GuideMarker } from "../guide/GuideMarker";
import { IssueDefendant, IssueRows } from "./IssueRows";
import { useJudgedRows } from "./JudgedRow";

const aCode = "A".charCodeAt(0);

export interface IssueEditorRowsProps {
  // the claim the issues are of: its chances sum them up
  claimId: string;
  issues: (Issue | IssueGroup)[];
  defendants: Defendant[];
  callbacks: IssueCallbacks;
  chances: RowChances;
}

/** One figure of a summary row. */
type SummaryFigure = {
  key: string;
  value: number | undefined;
  // against all or against any, rather than one defendant
  summary?: boolean;
  // the defendant settled: the claim is no longer decided against them
  settled?: boolean;
};

/**
 * A row's figures as the calculation has them: one per defendant, then
 * against all and against any where there are several.
 */
function figuresOf(
  chances: RowChances,
  rowId: string,
  defendants: Defendant[],
  summaryColumn: boolean,
): SummaryFigure[] {
  const figures = chances.of(rowId);
  return [
    ...defendants.map(({ id, settled }) => ({
      key: id,
      value: figures?.againstEach[id],
      settled,
    })),
    ...(summaryColumn
      ? [
          { key: "vAll", value: figures?.againstAll, summary: true },
          { key: "vAny", value: figures?.againstAny, summary: true },
        ]
      : []),
  ];
}

/** What a row of win chances comes to: its name, then one figure per column. */
const SummaryCells = ({
  rowId,
  title,
  caption,
  figures,
  calculating,
  emphasised = false,
}: {
  // the group or claim the row sums up
  rowId: string;
  title: string;
  caption: string;
  figures: SummaryFigure[];
  // a change is being calculated: the figures are those before it
  calculating: boolean;
  // the summary of the claim, where the one of a group is quiet
  emphasised?: boolean;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.claims" });
  const { t: tJudged } = useTranslation(undefined, { keyPrefix: "case.judged" });
  const sx = emphasised ? {} : { borderBottom: "none" };
  const figure = emphasised ? "body1" : "body2";
  const judged = useJudgedRows();

  // with a judgment the chances are history: this is what became of the row
  if (judged)
    return (
      <>
        <TableCell colSpan={emphasised ? 2 : 1} sx={sx}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {tJudged("judgment")}
          </Typography>
        </TableCell>
        {figures.map(({ key, summary, settled }) => {
          const upheld = judged.get(rowId)?.[key];
          return (
            <TableCell key={key} sx={sx}>
              {!summary && (
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {settled
                    ? t("settled")
                    : upheld === undefined
                      ? NO_VALUE
                      : tJudged(upheld ? "upheld" : "dismissed")}
                </Typography>
              )}
            </TableCell>
          );
        })}
      </>
    );

  return (
    <>
      <TableCell colSpan={emphasised ? 2 : 1} sx={sx}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {caption}
        </Typography>
      </TableCell>
      {figures.map(({ key, value, summary, settled }) => (
        <TableCell key={key} align={summary ? "right" : "left"} sx={sx}>
          {settled ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("settled")}
            </Typography>
          ) : (
            <Typography
              variant={figure}
              sx={{
                fontWeight: 700,
                opacity: calculating ? 0.5 : 1,
                transition: "opacity 0.2s",
                ...TABULAR_NUMERALS,
              }}
            >
              {value === undefined ? NO_VALUE : percentage.format(value)}
            </Typography>
          )}
        </TableCell>
      ))}
    </>
  );
};

export const IssueEditorRows = ({
  claimId,
  issues,
  defendants,
  callbacks,
  chances,
}: IssueEditorRowsProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.issues",
  });
  let ordinalNumber = 1;

  const summaryColumn = defendants.length > 1;
  // an empty column to indent grouped issues by, the issue itself, the
  // defendants and, with several of them, the two columns summing them up
  const columns = 2 + defendants.length + (summaryColumn ? 2 : 0);

  // the guide marks how issues combine at the first place they do
  let relationMarked = false;
  const relationRow = (issue: Issue, indented = false) => {
    const marked = !relationMarked;
    relationMarked = true;
    return (
      <TableRow>
        <TableCell
          // the indenting cell of a grouped issue runs down beside this row
          colSpan={indented ? columns - 1 : columns}
          sx={{ borderBottom: "none", py: 0.25 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <IssueRelationSelector
              onRelationClicked={(relation) =>
                callbacks.onRelationSelected(issue, relation)
              }
              selected={issue.relation}
            />
            {marked && <GuideMarker entry="relation" />}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  const groupRows = (
    issueGroup: IssueGroup,
    groupOrdinal: string,
    isFirst: boolean,
    isLast: boolean,
  ): ReactNode => {
    const subOrdinals = issueGroup.subIssues.map(
      (_, index) => `${groupOrdinal}.${String.fromCharCode(aCode + index)}`,
    );

    return (
      <Fragment key={"group_" + issueGroup.id}>
        <IssueGroupHeaderRow
          ordinal={groupOrdinal}
          relation={issueGroup.relation}
          groupedOrdinals={subOrdinals}
        />
        {issueGroup.subIssues.map((subIssue, index) => {
          const isFirstInGroup = index === 0;
          const isLastInGroup = index === issueGroup.subIssues.length - 1;

          // limit max win chances of defendants in XOR groups
          const issueDefendants =
            issueGroup.relation === IssueRelation.Xor
              ? defendants.map((d) => {
                  const issueDefendant: IssueDefendant = { ...d };
                  issueDefendant.maxWinChance = getXorWinChanceLimits(
                    issueGroup,
                    d,
                  )[subIssue.id];
                  return issueDefendant;
                })
              : defendants;
          return (
            <Fragment key={subIssue.id}>
              <IssueRows
                issue={subIssue}
                ordinal={subOrdinals[index]}
                callbacks={callbacks}
                defendants={issueDefendants}
                isFirst={isFirst && isFirstInGroup}
                isLast={isLast && isLastInGroup}
                indentRows={isLastInGroup ? 1 : 2}
                summaryColumn={summaryColumn}
                chances={chances}
              />
              {!isLastInGroup && relationRow(subIssue, true)}
            </Fragment>
          );
        })}
        <TableRow>
          <TableCell sx={{ borderBottom: "none" }} />
          <SummaryCells
            rowId={issueGroup.id}
            title={t("groups.summary.title")}
            caption={t("groups.summary.caption")}
            figures={figuresOf(
              chances,
              issueGroup.id,
              defendants,
              summaryColumn,
            )}
            calculating={chances.calculating}
          />
        </TableRow>
      </Fragment>
    );
  };

  return (
    <>
      <ClaimTableHead
        label={t("label")}
        firstColSpan={2}
        defendants={defendants}
        markers={{ label: "chances", summary: "summary" }}
        summary={
          summaryColumn
            ? {
                group: t("total"),
                first: t("againstAll"),
                second: t("againstAny"),
              }
            : undefined
        }
      />
      <TableBody>
        {issues.map((issue, index) => {
          const isFirst = index === 0;
          const isLast = index === issues.length - 1;
          const ordinal = `${ordinalNumber++}`;

          if (isGroup(issue)) {
            const lastOfGroup = issue.subIssues[issue.subIssues.length - 1];
            return (
              <Fragment key={"group_" + issue.id}>
                {groupRows(issue, ordinal, isFirst, isLast)}
                {!isLast && lastOfGroup && relationRow(lastOfGroup)}
              </Fragment>
            );
          }

          return (
            <Fragment key={issue.id}>
              <IssueRows
                issue={issue}
                ordinal={ordinal}
                callbacks={callbacks}
                defendants={defendants}
                isFirst={isFirst}
                isLast={isLast}
                summaryColumn={summaryColumn}
                chances={chances}
              />
              {!isLast && relationRow(issue)}
            </Fragment>
          );
        })}
        <TableRow
          sx={{
            bgcolor: "background.default",
            "& > td": { borderTop: 1, borderColor: "divider" },
          }}
        >
          <SummaryCells
            rowId={claimId}
            emphasised
            title={t("summary.title")}
            caption={t("summary.caption")}
            figures={figuresOf(chances, claimId, defendants, summaryColumn)}
            calculating={chances.calculating}
          />
        </TableRow>
      </TableBody>
    </>
  );
};

export interface QuantumCallbacks {
  /**
   * @param issue with the quantum
   * @param quantum quantum to edit or undefined if a new quantum shall be created
   */
  onQuantumEditorRequested: (issue: Issue, quantum?: Quantum) => void;
  onLiabilityUpdate: (
    issue: Issue,
    quantum: Quantum,
    defendantId: string,
    newLiability: number,
  ) => void;
  onQuantumDelete: (issue: Issue, quantum: Quantum) => void;
}

export interface IssueCallbacks {
  onIssueEditorRequested: (issue: Issue) => void;
  onDelete: (issue: Issue) => void;
  onMoveUp: (issue: Issue) => void;
  onMoveDown: (issue: Issue) => void;
  onRelationSelected: (issue: Issue, newRelation: IssueRelation) => void;
  onWinChanceUpdated: (
    issue: Issue,
    defendantId: string,
    newChance: number,
  ) => void;
  onGenericWinChanceUpdated: (issue: Issue, newChance: number) => void;
  onSwitchToGeneric: (issue: Issue) => void;
  onSwitchToSpecific: (issue: Issue) => void;
}
